import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import { getOnboardingThemeTokens, withAlpha } from "../themeTokens";
import { useMaterialUIController } from "context";

export function useInstallTheme() {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  return { t: getOnboardingThemeTokens({ darkMode }), darkMode };
}

const inputSx = (t, darkMode) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    minHeight: "52px",
    backgroundColor: darkMode ? "rgba(0,0,0,0.15)" : "#fff",
  },
});

export function WizardShell({ title, subtitle, stepLabel, children, onBack, backLabel = "Back", actions, t, darkMode }) {
  return (
    <Box
      maxWidth="480px"
      width="100%"
      mx="auto"
      px={{ xs: 2, sm: 3 }}
      pb={12}
      pt={3}
      sx={{ minHeight: "100dvh" }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        {onBack ? (
          <Button
            onClick={onBack}
            sx={{ minHeight: 48, color: t.primary, textTransform: "none", fontWeight: 600 }}
          >
            {backLabel}
          </Button>
        ) : (
          <Box />
        )}
        {stepLabel ? (
          <Typography variant="caption" sx={{ color: t.textMuted }}>
            {stepLabel}
          </Typography>
        ) : null}
      </Box>
      <Typography variant="h4" fontWeight={700} sx={{ color: t.textStrong, mb: 0.5 }}>
        {title}
      </Typography>
      {subtitle ? (
        <Typography variant="body2" sx={{ color: t.textMuted, mb: 3 }}>
          {subtitle}
        </Typography>
      ) : (
        <Box mb={2} />
      )}
      <Box role="region" aria-label={title}>
        {children}
      </Box>
      {actions ? (
        <Box
          position="fixed"
          bottom={0}
          left={0}
          right={0}
          display="flex"
          gap={1.5}
          px={2}
          py={1.5}
          sx={{
            maxWidth: "480px",
            mx: "auto",
            background: darkMode ? "rgba(15,23,42,0.94)" : "rgba(255,255,255,0.96)",
            borderTop: `1px solid ${t.borderSubtle}`,
            backdropFilter: "blur(8px)",
            pb: "calc(12px + env(safe-area-inset-bottom))",
          }}
        >
          {actions}
        </Box>
      ) : null}
    </Box>
  );
}

export function ChoiceCard({ title, description, selected, onClick, t }) {
  return (
    <Card
      component="button"
      type="button"
      onClick={onClick}
      aria-pressed={selected ? "true" : "false"}
      fullWidth
      sx={{
        display: "block",
        width: "100%",
        textAlign: "left",
        p: 2.5,
        mb: 1.5,
        minHeight: 72,
        cursor: "pointer",
        borderRadius: "16px",
        border: selected ? `2px solid ${t.primary}` : `1px solid ${t.borderSubtle}`,
        background: selected ? t.primarySoft : t.softSurface,
        color: t.textStrong,
        boxShadow: "none",
        "&:focus-visible": { outline: `3px solid ${t.focusRing}`, outlineOffset: 2 },
      }}
    >
      <Typography fontWeight={700} mb={0.5}>
        {title}
      </Typography>
      {description ? (
        <Typography variant="body2" sx={{ color: t.textMuted }}>
          {description}
        </Typography>
      ) : null}
    </Card>
  );
}

export function Field({ label, helperText, error, t, darkMode, ...props }) {
  return (
    <TextField
      label={label}
      error={Boolean(error)}
      helperText={error || helperText}
      fullWidth
      sx={inputSx(t, darkMode)}
      InputLabelProps={props.type === "date" ? { shrink: true } : undefined}
      {...props}
    />
  );
}

export function SelectField({ label, children, helperText, error, t, darkMode, ...props }) {
  return (
    <TextField select label={label} error={Boolean(error)} helperText={error || helperText} fullWidth sx={inputSx(t, darkMode)} {...props}>
      {children}
    </TextField>
  );
}

export function ToggleRow({ label, checked, onChange, description }) {
  return (
    <Box py={0.5}>
      <FormControlLabel
        control={<Checkbox checked={checked} onChange={(e) => onChange(e.target.checked)} inputProps={{ "aria-label": label }} />}
        label={<Typography fontWeight={600}>{label}</Typography>}
      />
      {description ? (
        <Typography variant="caption" display="block" ml={4} sx={{ opacity: 0.8 }}>
          {description}
        </Typography>
      ) : null}
    </Box>
  );
}

export function SummaryRow({ label, value, onEdit, t }) {
  return (
    <Box
      display="flex"
      alignItems="flex-start"
      justifyContent="space-between"
      gap={1}
      py={1.25}
      borderBottom={`1px solid ${t.divider}`}
    >
      <Box minWidth={0}>
        <Typography variant="caption" sx={{ color: t.textMuted, display: "block" }}>
          {label}
        </Typography>
        <Typography fontWeight={600} sx={{ color: t.textStrong, wordBreak: "break-word" }}>
          {value || "Not configured"}
        </Typography>
      </Box>
      {onEdit ? (
        <Button size="small" onClick={onEdit} sx={{ minHeight: 48, color: t.primary, textTransform: "none" }}>
          Edit
        </Button>
      ) : null}
    </Box>
  );
}

export function ErrorList({ errors, t }) {
  if (!errors?.length) return null;
  return (
    <Alert severity="error" sx={{ borderRadius: "12px", mb: 2 }} role="alert">
      {errors.map((e) => (
        <Typography key={e} variant="body2">
          {e}
        </Typography>
      ))}
    </Alert>
  );
}

export function StepAlert({ severity = "info", children }) {
  return <Alert severity={severity} sx={{ borderRadius: "12px", mb: 2 }}>{children}</Alert>;
}

export function PrimaryButton({ children, loading, disabled, onClick, t }) {
  return (
    <Button
      variant="contained"
      fullWidth
      disabled={disabled || loading}
      onClick={onClick}
      sx={{
        minHeight: 52,
        borderRadius: "12px",
        textTransform: "none",
        fontWeight: 700,
        fontSize: "1rem",
        background: `linear-gradient(135deg, ${t.primary}, ${withAlpha(t.primary, 0.82)})`,
        boxShadow: `0 4px 14px 0 ${withAlpha(t.primary, 0.39)}`,
        "&:disabled": { opacity: 0.55 },
      }}
    >
      {loading ? <CircularProgress size={20} color="inherit" /> : children}
    </Button>
  );
}

export function SecondaryButton({ children, onClick, disabled, t }) {
  return (
    <Button
      variant="outlined"
      fullWidth
      disabled={disabled}
      onClick={onClick}
      sx={{
        minHeight: 52,
        borderRadius: "12px",
        textTransform: "none",
        fontWeight: 600,
        color: t.primary,
        borderColor: withAlpha(t.primary, 0.5),
      }}
    >
      {children}
    </Button>
  );
}

export function ListSkeleton({ rows = 3, t }) {
  return (
    <Box>
      {Array.from({ length: rows }).map((_, i) => (
        <Box
          key={i}
          mb={1.5}
          p={2.5}
          borderRadius="16px"
          sx={{
            background: t.softSurface,
            border: `1px solid ${t.borderSubtle}`,
            height: 72,
          }}
        />
      ))}
    </Box>
  );
}
